import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../auth/AuthContext";
import { Button } from "./ui/button";
import { Sparkles, RefreshCw, TrendingUp, AlertTriangle, Eye, Target, HelpCircle } from "lucide-react";
import { toast } from "sonner";

export default function WeeklyInsights() {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/insights/weekly`);
      setInsights(r.data?.content || null);
    } catch (e) {}
  };

  const generate = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/insights/weekly`, {});
      setInsights(r.data);
      toast.success("Your week — read by Claude.");
    } catch (e) {
      toast.error("Couldn't generate insights yet.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <section className="rounded-2xl bg-white border border-[#eadfc9] p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Sparkles size={14} className="text-[#7a5fb0]" />
          <p className="font-mono-eyebrow text-[#7a5fb0]">Weekly pattern read</p>
        </div>
        <Button
          onClick={generate}
          disabled={loading}
          variant="ghost"
          size="sm"
          className="h-7 rounded-full text-[11px] text-[#7a5fb0] hover:bg-[#ede8f5]"
        >
          <RefreshCw size={11} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} />
          {loading ? "Reading…" : insights ? "Refresh" : "Generate"}
        </Button>
      </div>

      {!insights && !loading && (
        <p className="font-body text-[13px] text-[#6b7280]">
          Your coach will read your last 7 days of chats, habits, and milestones to surface what's working and where you've drifted.
        </p>
      )}

      {insights && (
        <div className="space-y-4">
          {insights.theme && (
            <div>
              <p className="font-mono-eyebrow text-[#6b7280] mb-1.5">This week's theme</p>
              <p className="font-display text-[18px] text-[#0f1724] leading-snug">
                {insights.theme}
              </p>
            </div>
          )}

          {insights.whats_working?.length > 0 && (
            <InsightBlock
              icon={TrendingUp}
              color="#3e6b52"
              bg="#e9f0ec"
              title="What's working"
              items={insights.whats_working}
            />
          )}

          {insights.drift?.length > 0 && (
            <InsightBlock
              icon={AlertTriangle}
              color="#c18848"
              bg="#f5ece0"
              title="Where you drifted"
              items={insights.drift}
            />
          )}

          {insights.hidden_pattern && (
            <div className="rounded-xl bg-[#ede8f5] border border-[#d9cfe8] p-4">
              <div className="flex items-center gap-2 mb-1.5">
                <Eye size={13} className="text-[#7a5fb0]" />
                <p className="font-mono-eyebrow text-[#7a5fb0]">Hidden pattern</p>
              </div>
              <p className="font-body text-[13px] text-[#2b3442] leading-relaxed">
                {insights.hidden_pattern}
              </p>
            </div>
          )}

          {insights.next_week_focus && (
            <div className="rounded-xl bg-[#e7edf5] border border-[#cdd8e8] p-4">
              <div className="flex items-center gap-2 mb-1.5">
                <Target size={13} className="text-[#486a8f]" />
                <p className="font-mono-eyebrow text-[#486a8f]">Next week focus</p>
              </div>
              <p className="font-body text-[14px] text-[#0f1724] leading-relaxed font-medium">
                {insights.next_week_focus}
              </p>
            </div>
          )}

          {insights.one_question && (
            <div className="pt-2 border-t border-[#f2ecdc]">
              <div className="flex items-start gap-2">
                <HelpCircle size={13} className="text-[#6b7280] mt-1 shrink-0" />
                <p className="font-body text-[13px] italic text-[#4b5563] leading-relaxed">
                  {insights.one_question}
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function InsightBlock({ icon: Icon, color, bg, title, items }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={12} style={{ color }} />
        <p className="font-mono-eyebrow" style={{ color }}>{title}</p>
      </div>
      <ul className="space-y-1.5">
        {items.map((t, i) => (
          <li key={i} className="flex gap-2 font-body text-[13px] text-[#2b3442] leading-relaxed">
            <span className="w-1 h-1 rounded-full mt-2 shrink-0" style={{ background: color }} />
            {t}
          </li>
        ))}
      </ul>
    </div>
  );
}
